import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Robot,
  Play,
  CircleNotch,
  CheckCircle,
  WarningCircle,
  XCircle,
  Clock,
  Calendar as CalIcon,
  Globe,
  MagicWand,
  Sliders,
  Sparkle,
} from "@phosphor-icons/react";
import { API, BACKEND_URL } from "@/lib/api";

const DAYS = [
  { v: 0, s: "Mon" },
  { v: 1, s: "Tue" },
  { v: 2, s: "Wed" },
  { v: 3, s: "Thu" },
  { v: 4, s: "Fri" },
  { v: 5, s: "Sat" },
  { v: 6, s: "Sun" },
];

// A short curated list of the most common timezones. The backend validates
// against pytz so any IANA zone is accepted — this is just the UI shortcut.
const TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Toronto",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Madrid",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
  "UTC",
];

const CAPTION_THEMES = ["Hormozi_1", "Hormozi_2", "Hormozi_3", "Beasty", "IMAN", "ALI_Dark"];

const hourLabel = (h) => {
  const ampm = h < 12 ? "AM" : "PM";
  const hh = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hh} ${ampm}`;
};

export default function AutoAgent() {
  const [settings, setSettings] = useState(null);
  const [preflight, setPreflight] = useState(null);
  const [runs, setRuns] = useState([]);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");
  const [savedFlash, setSavedFlash] = useState(false);
  const [topicsDraft, setTopicsDraft] = useState("");
  // Resources for long-form config
  const [baseVideos, setBaseVideos] = useState([]);
  const [twins, setTwins] = useState([]);
  const [refIngests, setRefIngests] = useState([]);

  const load = async () => {
    try {
      const [s, p, r, bv, tw, ri] = await Promise.all([
        axios.get(`${API}/auto-agent/settings`),
        axios.get(`${API}/auto-agent/preflight`),
        axios.get(`${API}/auto-agent/runs`),
        axios.get(`${API}/base-videos`).catch(() => ({ data: { videos: [] } })),
        axios.get(`${API}/ai-twin/images`).catch(() => ({ data: { images: [] } })),
        axios.get(`${API}/reference/ingests`).catch(() => ({ data: { ingests: [] } })),
      ]);
      setSettings(s.data);
      setPreflight(p.data);
      setRuns(r.data.runs || []);
      setTopicsDraft((s.data?.topics?.list || []).join("\n"));
      setBaseVideos((bv.data.videos || []).filter((b) => (b.status || "ready") === "ready"));
      setTwins(tw.data.images || []);
      setRefIngests(ri.data.ingests || []);
    } catch (e) {
      setError(e.response?.data?.detail || "Couldn't load Auto Agent settings");
    }
  };

  useEffect(() => { load(); }, []);

  const totalPerRun = useMemo(() => {
    if (!settings) return 0;
    const c = settings.content || {};
    return (c.auto_count || 0) + (c.lifestyle_count || 0) + (c.motion_remix_count || 0) + (c.long_form_count || 0);
  }, [settings]);

  const daysLabel = useMemo(() => {
    if (!settings) return "";
    const picked = settings.schedule?.days_of_week || [];
    if (picked.length === 7) return "Every day";
    if (picked.length === 5 && [0, 1, 2, 3, 4].every((d) => picked.includes(d))) return "Weekdays";
    if (picked.length === 2 && picked.includes(5) && picked.includes(6)) return "Weekends";
    return picked.map((d) => DAYS[d]?.s).join(", ");
  }, [settings]);

  const updateField = (path, value) => {
    setSettings((prev) => {
      const next = structuredClone(prev);
      const parts = path.split(".");
      let target = next;
      for (let i = 0; i < parts.length - 1; i++) target = target[parts[i]];
      target[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const toggleDay = (d) => {
    setSettings((prev) => {
      const picked = new Set(prev.schedule?.days_of_week || []);
      if (picked.has(d)) picked.delete(d); else picked.add(d);
      const next = structuredClone(prev);
      next.schedule.days_of_week = [...picked].sort((a, b) => a - b);
      return next;
    });
  };

  const save = async (withTopics = true) => {
    setSaving(true); setError("");
    try {
      const payload = structuredClone(settings);
      if (withTopics) {
        payload.topics = {
          ...(payload.topics || {}),
          list: topicsDraft.split("\n").map((s) => s.trim()).filter(Boolean),
        };
      }
      const r = await axios.put(`${API}/auto-agent/settings`, payload);
      setSettings(r.data);
      // Refresh preflight since credits / config may have changed
      const p = await axios.get(`${API}/auto-agent/preflight`);
      setPreflight(p.data);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1600);
    } catch (e) {
      setError(e.response?.data?.detail || "Couldn't save. Check the config above.");
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    setRunning(true); setError("");
    try {
      await axios.post(`${API}/auto-agent/run-now`);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Run failed to start");
    } finally {
      setRunning(false);
    }
  };

  if (!settings) {
    return <div className="flex items-center justify-center py-20"><CircleNotch size={24} className="animate-spin text-[#E91E8C]" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="auto-agent-page">
      {/* Header + hero toggle */}
      <div className="card p-6 space-y-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#E91E8C] to-[#c91a7b] flex items-center justify-center shadow-lg">
              <Robot size={26} weight="fill" className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-zinc-900">Auto Content Agent</h1>
              <p className="text-sm text-zinc-500 mt-0.5">Wake up to fresh content every day. Set it once, we run it for you.</p>
            </div>
          </div>
          <label className="flex items-center gap-3 cursor-pointer bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-2.5" data-testid="auto-agent-enable-toggle">
            <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">Agent</span>
            <input
              type="checkbox"
              checked={!!settings.enabled}
              onChange={(e) => updateField("enabled", e.target.checked)}
              className="sr-only peer"
              data-testid="auto-agent-enabled-input"
            />
            <div className="relative w-11 h-6 bg-zinc-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#E91E8C]"></div>
            <span className={`text-sm font-bold ${settings.enabled ? "text-[#E91E8C]" : "text-zinc-400"}`}>{settings.enabled ? "ON" : "OFF"}</span>
          </label>
        </div>

        {settings.enabled && (
          <div className="flex items-center flex-wrap gap-3 text-xs bg-gradient-to-r from-pink-50 to-rose-50 border border-pink-200 rounded-lg p-3">
            <Clock size={14} weight="fill" className="text-[#E91E8C]" />
            <span className="text-zinc-700"><b>{hourLabel(settings.schedule.hour)}</b> · {daysLabel} · <span className="text-zinc-500">{settings.schedule.timezone}</span></span>
            <span className="text-zinc-400">·</span>
            <span className="text-zinc-700"><b>{totalPerRun}</b> video{totalPerRun !== 1 ? "s" : ""} per run</span>
          </div>
        )}
      </div>

      {/* Preflight status */}
      {preflight && (
        <div className={`card p-4 border-l-4 ${preflight.ok ? "border-emerald-500" : "border-amber-500"}`} data-testid="auto-agent-preflight">
          <div className="flex items-start gap-3">
            {preflight.ok
              ? <CheckCircle size={22} weight="fill" className="text-emerald-500 shrink-0 mt-0.5" />
              : <WarningCircle size={22} weight="fill" className="text-amber-500 shrink-0 mt-0.5" />}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-zinc-900">{preflight.ok ? "Ready to run" : "Heads up — something's off"}</p>
              <p className="text-xs text-zinc-600 mt-0.5 break-words">{preflight.reason}</p>
              {preflight.details && Object.keys(preflight.details).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {Object.entries(preflight.details).map(([k, v]) => (
                    <span key={k} className="text-[10px] uppercase tracking-wider text-zinc-500 bg-zinc-100 px-2 py-1 rounded">
                      {k.replace(/_/g, " ")}: <b className="text-zinc-700">{String(v)}</b>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Schedule */}
      <div className="card p-5 space-y-4" data-testid="auto-agent-schedule-section">
        <h2 className="text-sm font-bold uppercase tracking-wider text-zinc-500 flex items-center gap-2">
          <CalIcon size={14} weight="fill" className="text-[#E91E8C]" /> Schedule
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 block mb-1">Time of day</label>
            <div className="flex items-center gap-2">
              <select value={settings.schedule.hour} onChange={(e) => updateField("schedule.hour", parseInt(e.target.value, 10))} className="input-field text-sm" data-testid="auto-agent-hour">
                {Array.from({ length: 24 }).map((_, h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
              </select>
              <select value={settings.schedule.minute} onChange={(e) => updateField("schedule.minute", parseInt(e.target.value, 10))} className="input-field text-sm w-24" data-testid="auto-agent-minute">
                {[0, 15, 30, 45].map((m) => <option key={m} value={m}>:{String(m).padStart(2, "0")}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 block mb-1">Timezone</label>
            <select value={settings.schedule.timezone} onChange={(e) => updateField("schedule.timezone", e.target.value)} className="input-field text-sm" data-testid="auto-agent-tz">
              {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 block mb-1">Days</label>
            <div className="flex flex-wrap gap-1">
              {DAYS.map((d) => {
                const picked = (settings.schedule.days_of_week || []).includes(d.v);
                return (
                  <button
                    key={d.v}
                    type="button"
                    onClick={() => toggleDay(d.v)}
                    className={`px-2.5 py-1.5 text-xs font-semibold rounded-md border transition-all ${
                      picked ? "bg-[#E91E8C] text-white border-[#E91E8C]" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"
                    }`}
                    data-testid={`auto-agent-day-${d.v}`}
                  >
                    {d.s}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Content mix */}
      <div className="card p-5 space-y-4" data-testid="auto-agent-content-section">
        <h2 className="text-sm font-bold uppercase tracking-wider text-zinc-500 flex items-center gap-2">
          <Sliders size={14} weight="fill" className="text-[#E91E8C]" /> Content per run
        </h2>

        {["auto_count", "lifestyle_count", "motion_remix_count", "long_form_count"].map((field) => {
          const labels = {
            auto_count: ["Auto Create", "Talking Photo · Realistic mode (max likeness, primary model)", 15],
            lifestyle_count: ["Lifestyle Videos", "Optional · prompted b-roll scenes (no audio / no script)", 10],
            motion_remix_count: ["Motion Remix", "Re-animate one of your existing clips onto your AI Twin", 5],
            long_form_count: ["Long-Form", "YouTube-length videos (~20-40 min render each)", 3],
          };
          const [label, desc, max] = labels[field];
          const val = settings.content?.[field] || 0;
          return (
            <div key={field} className="flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-zinc-900">{label}</p>
                <p className="text-[11px] text-zinc-500">{desc}</p>
              </div>
              <input
                type="number"
                min={0}
                max={max}
                value={val}
                onChange={(e) => updateField(`content.${field}`, Math.max(0, Math.min(max, parseInt(e.target.value || "0", 10))))}
                style={{ width: "5rem" }}
                className="input-field text-sm text-center shrink-0"
                data-testid={`auto-agent-${field.replace(/_/g, "-")}`}
              />
            </div>
          );
        })}
        <p className="text-[11px] text-zinc-400">Total per run: <b className="text-zinc-700">{totalPerRun}</b>. Captions & B-roll toggles apply to Auto Create items.</p>

        {/* Render mode is locked to Talking Photo + Realistic for Auto Agent
            batches so every daily render uses the highest-likeness pipeline
            (Magic Hour /ai-talking-photo with generation_mode="realistic"). */}
        <div className="mt-1 rounded-lg bg-[#FDF2F8] border border-[#F5D0E9] px-3 py-2 flex items-start gap-2" data-testid="auto-agent-render-mode-banner">
          <span className="text-[#E91E8C] text-xs font-bold mt-0.5">◆</span>
          <p className="text-[11px] text-zinc-700 leading-snug">
            <b>Render mode:</b> Talking Photo + <b>Realistic</b> (locked) for the best AI Twin likeness.
          </p>
        </div>

        {/* Video engine — Auto (best value) vs HeyGen HD (premium) */}
        <div className="mt-3" data-testid="auto-agent-video-engine">
          <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 block mb-1.5">Video engine</label>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => updateField("content.video_engine", "auto")}
              className={`p-2.5 rounded-lg border text-left transition-all ${(settings.content?.video_engine || "auto") !== "heygen" ? "border-emerald-500 bg-emerald-50" : "border-zinc-200 hover:border-zinc-300"}`}
              data-testid="auto-agent-engine-auto"
            >
              <p className="text-xs font-bold text-zinc-900 leading-tight">Auto ✨</p>
              <p className="text-[10px] text-zinc-500 leading-tight mt-0.5">Best value (Pruna)</p>
            </button>
            <button
              type="button"
              onClick={() => updateField("content.video_engine", "heygen")}
              className={`p-2.5 rounded-lg border text-left transition-all ${settings.content?.video_engine === "heygen" ? "border-violet-500 bg-violet-50" : "border-zinc-200 hover:border-zinc-300"}`}
              data-testid="auto-agent-engine-heygen"
            >
              <div className="flex items-center gap-1">
                <p className="text-xs font-bold text-zinc-900 leading-tight">💎 HeyGen</p>
                <span className="text-[8px] font-bold px-1 py-0.5 rounded bg-violet-200 text-violet-800">HD</span>
              </div>
              <p className="text-[10px] text-zinc-500 leading-tight mt-0.5">Premium · uses more credits</p>
            </button>
          </div>
          {settings.content?.video_engine === "heygen" && (
            <p className="mt-1.5 text-[10px] leading-tight text-violet-800 bg-violet-50 border border-violet-200 rounded p-1.5" data-testid="auto-agent-heygen-note">
              💎 Every daily render uses HeyGen HD (1080p, ~$0.05/sec) and extra video credits. Keep your HeyGen wallet funded.
            </p>
          )}
        </div>

        {/* Long-form config appears only when long_form_count > 0 so the
            panel isn't noisy for users who don't care about YouTube-length. */}
        {(settings.content?.long_form_count || 0) > 0 && (
          <div className="mt-4 border-t border-zinc-200 pt-4 space-y-3" data-testid="auto-agent-long-form-config">
            <p className="text-[11px] font-bold uppercase tracking-wider text-[#E91E8C]">Long-Form details</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 block mb-1">Target length (min)</label>
                <input
                  type="number"
                  min={2}
                  max={30}
                  value={settings.content?.long_form_target_minutes || 5}
                  onChange={(e) => updateField("content.long_form_target_minutes", Math.max(2, Math.min(30, parseInt(e.target.value || "5", 10))))}
                  className="input-field text-sm"
                  data-testid="auto-agent-lf-target-minutes"
                />
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 block mb-1">Pipeline</label>
                <select
                  value={settings.content?.long_form_pipeline_mode || "lip_sync"}
                  onChange={(e) => updateField("content.long_form_pipeline_mode", e.target.value)}
                  className="input-field text-sm"
                  data-testid="auto-agent-lf-pipeline"
                >
                  <option value="lip_sync">Lip Sync — rotates base videos</option>
                  <option value="talking_photo">Talking Photo — static AI Twin</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 block mb-1">Edit style source (optional)</label>
                <select
                  value={settings.content?.long_form_reference_ingest_id || ""}
                  onChange={(e) => updateField("content.long_form_reference_ingest_id", e.target.value || null)}
                  className="input-field text-sm"
                  data-testid="auto-agent-lf-reference"
                >
                  <option value="">None — default style</option>
                  {refIngests.map((r) => (
                    <option key={r.id} value={r.id}>
                      {(r.kind === "upload" ? "📼 " : "🔗 ") + (r.title || r.platform || r.id).toString().slice(0, 50)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {settings.content?.long_form_pipeline_mode === "lip_sync" ? (
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 block mb-1">Base videos (pick 1–5, rotated)</label>
                {baseVideos.length === 0 ? (
                  <p className="text-[11px] text-zinc-500 bg-zinc-50 border border-zinc-200 rounded p-2.5">No base videos yet — create some in AI Twin Gallery first.</p>
                ) : (
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                    {baseVideos.slice(0, 18).map((b) => {
                      const ids = settings.content?.long_form_base_video_ids || [];
                      const picked = ids.includes(b.id);
                      const toggle = () => {
                        let next;
                        if (picked) next = ids.filter((x) => x !== b.id);
                        else if (ids.length >= 5) next = ids;
                        else next = [...ids, b.id];
                        updateField("content.long_form_base_video_ids", next);
                      };
                      return (
                        <button
                          key={b.id}
                          type="button"
                          onClick={toggle}
                          className={`relative rounded-md overflow-hidden border-2 transition-all ${
                            picked ? "border-[#E91E8C] ring-2 ring-[#E91E8C]/30" : "border-zinc-200 hover:border-zinc-300"
                          }`}
                          data-testid={`auto-agent-lf-base-${b.id}`}
                        >
                          <video src={`${BACKEND_URL}/api/base-videos/${b.id}/file`} className="w-full aspect-video object-cover bg-zinc-900 pointer-events-none" muted playsInline preload="metadata" />
                          {picked && (
                            <span className="absolute top-0.5 left-0.5 bg-[#E91E8C] text-white text-[9px] font-bold px-1 rounded">#{ids.indexOf(b.id) + 1}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 block mb-1">AI Twin image</label>
                {twins.length === 0 ? (
                  <p className="text-[11px] text-zinc-500 bg-zinc-50 border border-zinc-200 rounded p-2.5">No AI Twin images yet — create one in AI Twin Gallery.</p>
                ) : (
                  <div className="grid grid-cols-6 md:grid-cols-10 gap-1.5">
                    {twins.slice(0, 20).map((img) => {
                      const picked = settings.content?.long_form_image_id === img.id;
                      return (
                        <button
                          key={img.id}
                          type="button"
                          onClick={() => updateField("content.long_form_image_id", img.id)}
                          className={`aspect-square rounded-md overflow-hidden border-2 ${
                            picked ? "border-[#E91E8C] ring-2 ring-[#E91E8C]/30" : "border-zinc-200 hover:border-zinc-300"
                          }`}
                          data-testid={`auto-agent-lf-twin-${img.id}`}
                        >
                          <img src={`${BACKEND_URL}/api/ai-twin/images/${img.filename}`} alt="" className="w-full h-full object-cover" />
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2.5">
              <b>Heads up:</b> each long-form video takes ~20-40 min to render and costs ~$1-3. Keep the count low unless you're sure.
            </p>
          </div>
        )}
      </div>

      {/* Topics */}
      <div className="card p-5 space-y-4" data-testid="auto-agent-topics-section">
        <h2 className="text-sm font-bold uppercase tracking-wider text-zinc-500 flex items-center gap-2">
          <Sparkle size={14} weight="fill" className="text-[#E91E8C]" /> Topics
        </h2>
        <div className="flex items-center gap-2">
          {[["ai", "AI generator (no repeats)"], ["list", "My own list"]].map(([v, lbl]) => (
            <button
              key={v}
              type="button"
              onClick={() => updateField("topics.mode", v)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md border transition-all ${
                settings.topics?.mode === v
                  ? "bg-[#E91E8C] text-white border-[#E91E8C]"
                  : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"
              }`}
              data-testid={`auto-agent-topic-mode-${v}`}
            >
              {lbl}
            </button>
          ))}
        </div>

        {settings.topics?.mode === "list" ? (
          <>
            <textarea
              value={topicsDraft}
              onChange={(e) => setTopicsDraft(e.target.value)}
              rows={6}
              placeholder="One topic per line — the agent consumes them in order and resumes where it left off."
              className="input-field text-sm resize-none font-mono"
              data-testid="auto-agent-topics-list"
            />
            <p className="text-[11px] text-zinc-400">
              {settings.topics?.list?.length || 0} topic(s) saved · next up: row {Math.min((settings.topics?.cursor || 0) + 1, (settings.topics?.list?.length || 0) + 1)}.
              When we run out of your list we auto-generate fresh ones (no repeats from your history).
            </p>
          </>
        ) : (
          <div className="text-xs text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-lg p-3">
            We'll generate fresh topics from your Brand Knowledge and skip anything that's already in your Content Pipeline or past bulk runs — <b>no repeats ever</b>.
          </div>
        )}
      </div>

      {/* Styling */}
      <div className="card p-5 space-y-4" data-testid="auto-agent-style-section">
        <h2 className="text-sm font-bold uppercase tracking-wider text-zinc-500 flex items-center gap-2">
          <MagicWand size={14} weight="fill" className="text-[#E91E8C]" /> Styling
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={!!settings.captions_enabled}
              onChange={(e) => updateField("captions_enabled", e.target.checked)}
              className="w-4 h-4 rounded border-zinc-300 text-[#E91E8C] focus:ring-[#E91E8C]"
              data-testid="auto-agent-captions-toggle"
            />
            <span className="text-sm font-semibold text-zinc-900">Burn-in captions</span>
          </label>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 block mb-1">Caption theme</label>
            <select value={settings.caption_theme} onChange={(e) => updateField("caption_theme", e.target.value)} disabled={!settings.captions_enabled} className="input-field text-sm disabled:opacity-40" data-testid="auto-agent-caption-theme">
              {CAPTION_THEMES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={!!settings.broll_enabled}
              onChange={(e) => updateField("broll_enabled", e.target.checked)}
              className="w-4 h-4 rounded border-zinc-300 text-[#E91E8C] focus:ring-[#E91E8C]"
              data-testid="auto-agent-broll-toggle"
            />
            <span className="text-sm font-semibold text-zinc-900">Auto B-roll</span>
          </label>
        </div>
      </div>

      {/* Actions */}
      {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="auto-agent-error">{error}</div>}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => save(true)}
          disabled={saving}
          className="bg-[#E91E8C] hover:bg-[#c91a7b] text-white font-bold px-6 py-2.5 rounded-xl transition-all disabled:opacity-50 flex items-center gap-2"
          data-testid="auto-agent-save-btn"
        >
          {saving ? <CircleNotch size={16} className="animate-spin" /> : null}
          {savedFlash ? "Saved ✓" : "Save settings"}
        </button>
        <button
          onClick={runNow}
          disabled={running || !preflight?.ok}
          title={!preflight?.ok ? preflight?.reason : "Trigger a run right now"}
          className="bg-white hover:bg-zinc-50 text-zinc-900 border border-zinc-300 font-bold px-6 py-2.5 rounded-xl transition-all disabled:opacity-50 flex items-center gap-2"
          data-testid="auto-agent-run-now-btn"
        >
          {running ? <CircleNotch size={16} className="animate-spin" /> : <Play size={16} weight="fill" />}
          Run now
        </button>
        <button
          onClick={async () => {
            const p = await axios.get(`${API}/auto-agent/preflight`);
            setPreflight(p.data);
          }}
          className="text-sm text-zinc-500 hover:text-[#E91E8C] px-3 py-2 flex items-center gap-1"
          data-testid="auto-agent-recheck-btn"
        >
          <Globe size={14} /> Re-check credits
        </button>
      </div>

      {/* Runs history */}
      <div className="card p-5 space-y-3" data-testid="auto-agent-runs-section">
        <h2 className="text-sm font-bold uppercase tracking-wider text-zinc-500 flex items-center gap-2">
          <Clock size={14} weight="fill" className="text-[#E91E8C]" /> Recent runs
        </h2>
        {runs.length === 0 ? (
          <p className="text-sm text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-lg p-4 text-center">
            No runs yet. Flip the agent ON and pick a time — or hit "Run now" for an instant test.
          </p>
        ) : (
          <div className="space-y-2">
            {runs.map((run) => {
              const Icon = run.status === "started" ? CheckCircle : run.status === "skipped" ? WarningCircle : XCircle;
              const tone = run.status === "started" ? "text-emerald-600" : run.status === "skipped" ? "text-amber-600" : "text-red-600";
              const when = new Date(run.at).toLocaleString();
              return (
                <div key={run.id} className="flex items-start gap-3 p-3 bg-zinc-50 border border-zinc-200 rounded-lg" data-testid={`auto-agent-run-${run.id}`}>
                  <Icon size={18} weight="fill" className={`${tone} shrink-0 mt-0.5`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold uppercase tracking-wider text-zinc-500">{run.status}{run.manual ? " · manual" : ""} · {when}</p>
                    <p className="text-sm text-zinc-700 mt-0.5 break-words">{run.reason}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
